import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector, useStore } from 'react-redux';
import {
    Alert, Avatar, Badge, Box, Button, CircularProgress, Dialog, IconButton,
    Popover, Typography, useMediaQuery, useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/axiosConfig';
import { useNotificationSync } from '../../hooks/useNotificationSync';
import { notifications as platformNotifications } from '../../platform';

const isUnread = notification => !Number(notification.is_read);
const newestFirst = (a, b) => new Date(b.created_at) - new Date(a.created_at) || Number(b.id) - Number(a.id);
const withinBoundary = (notification, data) => data.throughId == null || Number(notification.id) <= Number(data.throughId);

function NotificationInbox() {
    const theme = useTheme();
    const mobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const store = useStore();
    const { openInboxSync, setupNotificationListener } = useNotificationSync();
    const [anchor, setAnchor] = useState(null);
    const [rows, setRows] = useState([]);
    const [newIds, setNewIds] = useState(new Set());
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const openRef = useRef(false);
    const opening = useRef(false);
    const pendingRefresh = useRef(false);
    const mounted = useRef(true);
    const countSequence = useRef(0);
    const listSequence = useRef(0);
    const requests = useRef(new Set());
    const bellButton = useRef(null);
    const closeButton = useRef(null);
    const initiatingAuth = useRef(store.getState().auth);
    const currentSession = useCallback(() => {
        const auth = store.getState().auth;
        return mounted.current && auth.isAuthenticated && auth.token === initiatingAuth.current.token
            && auth.user?.user === initiatingAuth.current.user?.user;
    }, [store]);

    const get = useCallback(async path => {
        const controller = new AbortController();
        requests.current.add(controller);
        try { return await api.get(path, { signal: controller.signal }); }
        finally { requests.current.delete(controller); }
    }, []);

    const refreshCount = useCallback(async () => {
        const sequence = ++countSequence.current;
        try {
            const response = await get('/api/notifications/unread-count');
            if (currentSession() && sequence === countSequence.current) setUnreadCount(response.data.unreadCount);
        } catch { /* Keep the last known badge on temporary connectivity failures. */ }
    }, [get, currentSession]);

    const refreshList = useCallback(async () => {
        if (!openRef.current) return;
        if (opening.current) { pendingRefresh.current = true; return; }
        const sequence = ++listSequence.current;
        try {
            const response = await get('/api/notifications?limit=50&sort=recent');
            if (currentSession() && openRef.current && sequence === listSequence.current) {
                setRows(response.data.notifications);
                setError(null);
            }
        } catch {
            if (currentSession() && openRef.current && sequence === listSequence.current) {
                setError('Could not refresh notifications. Your current list is still shown.');
            }
        }
    }, [get, currentSession]);

    useEffect(() => {
        mounted.current = true;
        const abort = () => requests.current.forEach(controller => controller.abort());
        const unsubscribe = store.subscribe(() => { if (!currentSession()) abort(); });
        refreshCount();
        const timer = setInterval(refreshCount, 30000);
        return () => { mounted.current = false; abort(); unsubscribe(); clearInterval(timer); };
    }, [store, currentSession, refreshCount]);

    useEffect(() => {
        const received = () => { refreshCount(); refreshList(); };
        const stopPush = platformNotifications.onReceived(received);
        const stopSync = setupNotificationListener(({ eventType, data = {} }) => {
            if (!currentSession()) return;
            if (eventType === 'newNotification') { received(); return; }
            if (eventType === 'dismiss') setRows(previous => previous.filter(row => row.id !== data.notificationId));
            if (eventType === 'markAsRead') setRows(previous => previous.map(row => row.id === data.notificationId ? { ...row, is_read: true } : row));
            if (eventType === 'markAllAsRead') setRows(previous => previous.map(row => withinBoundary(row, data) ? { ...row, is_read: true } : row));
            // Keep the opening snapshot's highlights even after a read event.
            refreshCount();
        });
        return () => { stopPush?.(); stopSync(); };
    }, [setupNotificationListener, refreshCount, refreshList, currentSession]);

    const openInbox = async event => {
        if (opening.current) {
            openRef.current = true;
            setAnchor(event.currentTarget);
            return;
        }
        openRef.current = true;
        opening.current = true;
        pendingRefresh.current = false;
        ++listSequence.current;
        ++countSequence.current;
        setAnchor(event.currentTarget);
        setRows([]);
        setNewIds(new Set());
        setError(null);
        setLoading(true);
        const snapshot = await openInboxSync(api);
        opening.current = false;
        if (!currentSession()) return;
        if (snapshot) {
            if (openRef.current) {
                setNewIds(new Set(snapshot.notifications.filter(isUnread).map(row => String(row.id))));
                setRows(snapshot.notifications.map(row => ({ ...row, is_read: true })));
            }
            refreshCount();
        } else if (openRef.current) {
            setError('Could not open notifications. Check your connection and try again.');
        }
        setLoading(false);
        if (snapshot && pendingRefresh.current) refreshList();
    };

    const close = () => {
        openRef.current = false;
        ++listSequence.current;
        setAnchor(null);
    };
    const goToHistory = () => { close(); navigate('/profile/notifications'); };
    const fresh = row => newIds.has(String(row.id)) || isUnread(row);
    const groups = [
        ['New for you', rows.filter(fresh).sort(newestFirst)],
        ['Earlier', rows.filter(row => !fresh(row)).sort(newestFirst)]
    ];
    const content = <>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, minHeight: 64, flexShrink: 0 }}>
            {mobile && <IconButton ref={closeButton} aria-label="Back from notifications" onClick={close} sx={{ width: 48, height: 48 }}><ArrowBackIcon /></IconButton>}
            <Typography id="notification-inbox-title" component="h2" variant="h6" sx={{ fontWeight: 700, flex: 1, pl: mobile ? 0 : 1 }}>Notifications</Typography>
            {!mobile && <IconButton ref={closeButton} aria-label="Close notifications" onClick={close} sx={{ width: 44, height: 44 }}><CloseIcon /></IconButton>}
        </Box>
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overscrollBehavior: 'contain', px: { xs: 0, sm: 1 }, pb: 2 }} aria-busy={loading}>
            {loading ? <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress size={28} aria-label="Loading notifications" /></Box> : <>
                {error && <Alert severity="error" sx={{ mx: 2, my: 1 }}>{error}</Alert>}
                {!error && !rows.length && <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
                    <NotificationsNoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6">You're all caught up</Typography>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>Your notifications will appear here.</Typography>
                </Box>}
                {groups.map(([label, items]) => items.length > 0 && <Box component="section" key={label} aria-label={label}>
                    <Typography component="h3" variant="subtitle2" sx={{ px: 2.5, pt: 2, pb: 1, color: 'text.secondary', fontWeight: 700 }}>{label}</Typography>
                    {label === 'New for you'}
                    <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                        {items.map(row => {
                            const highlighted = fresh(row);
                            const color = row.type === 'error' || row.priority === 'urgent' ? 'error' : row.type === 'warning' ? 'warning' : row.type === 'success' ? 'success' : 'primary';
                            const Icon = color === 'error' ? ErrorOutlineIcon : color === 'warning' ? WarningAmberIcon : color === 'success' ? CheckCircleOutlineIcon : NotificationsNoneIcon;
                            const created = new Date(row.created_at);
                            return <Box component="li" key={row.id} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, px: 2.5, py: 2,
                                bgcolor: highlighted ? alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.055) : 'transparent',
                                borderRadius: { xs: 0, sm: 2 }, position: 'relative' }}>
                                {highlighted && <Box component="span" aria-label={isUnread(row) ? 'Unread notification' : 'New when you opened notifications'} sx={{ position: 'absolute', left: 7, top: 36, width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />}
                                <Avatar sx={{ width: 42, height: 42, mt: 0.25, bgcolor: alpha(theme.palette[color].main, 0.13), color: `${color}.main` }}><Icon /></Avatar>
                                <Box sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}>
                                    <Typography component="p" variant="subtitle2" sx={{ fontWeight: highlighted ? 700 : 600 }}>{row.title}</Typography>
                                    <Typography variant="body2" sx={{ mt: 0.4, lineHeight: 1.5 }}>{row.message}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                                        {Number.isNaN(created.getTime()) ? '' : formatDistanceToNow(created, { addSuffix: true })}
                                        {['urgent', 'high'].includes(row.priority) && ` · ${row.priority === 'urgent' ? 'Urgent' : 'High priority'}`}
                                    </Typography>
                                </Box>
                            </Box>;
                        })}
                    </Box>
                </Box>)}
            </>}
        </Box>
        <Box sx={{ flexShrink: 0, px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
            <Button fullWidth onClick={goToHistory} sx={{ minHeight: 48 }}>View all notifications</Button>
        </Box>
    </>;

    return <>
        <IconButton ref={bellButton} color="inherit" onClick={openInbox} aria-busy={loading} sx={{ mr: 1, minWidth: 44, minHeight: 44 }}
            aria-label={`${unreadCount} unread notifications`} aria-haspopup="dialog" aria-expanded={Boolean(anchor)}>
            <Badge badgeContent={unreadCount} color="error" max={99}>{unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}</Badge>
        </IconButton>
        {mobile ? <Dialog fullScreen open={Boolean(anchor)} onClose={close} aria-labelledby="notification-inbox-title"
            TransitionProps={{ onEntered: () => closeButton.current?.focus(), onExited: () => bellButton.current?.focus() }}
            PaperProps={{ sx: { height: '100dvh', maxHeight: '100dvh', bgcolor: 'background.default',
                pt: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
                pb: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
                pl: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
                pr: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))' } }}>{content}</Dialog>
            : <Popover open={Boolean(anchor)} anchorEl={anchor} onClose={close}
                TransitionProps={{ onEntered: () => closeButton.current?.focus(), onExited: () => bellButton.current?.focus() }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ role: 'dialog', 'aria-labelledby': 'notification-inbox-title', sx: { width: 440, height: 600,
                    maxHeight: 'calc(100dvh - 32px)', display: 'flex', flexDirection: 'column', borderRadius: 3 } }}>{content}</Popover>}
    </>;
}

// A pending inbox request must never populate a different account's panel.
export default function NotificationBell() {
    const auth = useSelector(state => state.auth);
    return auth.isAuthenticated ? <NotificationInbox key={`${auth.user?.user}:${auth.token}`} /> : null;
}
